import Paginator from "components/Paginator";
import {ChangeEvent, FC, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import useAwaitableModal from "hooks/useAwaitableModal";
import useRecruiter from "contexts/User/useRecruiter";
import Ordering from "./Ordering";
import NewEmployeeModal from "./modals/NewEmployee";
import Employee from "./Employee";
import {IEnterpriseRecruiter} from "api/types";
import useEmployeesContext from "contexts/Employees/useEmployees";
import {Search, UserPlus} from "lucide-react";

const EmployeesPage: FC = () => {
  let {page, pages, setPage, loading, employees, scroll, total} = useEmployeesContext();
  const [addEmployee, AddEmployee] = useAwaitableModal(NewEmployeeModal, {});
  const navigate = useNavigate();

  const [sp, setSp] = useSearchParams();
  const [search, setSearch] = useState(sp.get("search") || "");
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchChangeTimeout = useRef<NodeJS.Timeout>();

  const user = useRecruiter() as IEnterpriseRecruiter;

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    if (searchChangeTimeout.current) clearTimeout(searchChangeTimeout.current);
    setSearch(e.target.value);
    searchChangeTimeout.current = setTimeout(() => {
      setSp((old) => {
        old.set("search", e.target.value);
        return old;
      });
    }, 500);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scroll.current = el.scrollTop;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scroll({top: scroll.current, behavior: "auto"});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef.current]);

  const onNewEmployee = () => {
    addEmployee()
      .then((created) => {
        if (created) navigate(0);
      })
      .catch(() => {});
  };

  const limitReached = user.maxEmployees === user.employees;

  const onIncreaseEmployees = () => {
    const email = "anthonybirch@akadian.com";
    const subject = "Increase Employees";
    const body =
      "Hi Anthony, I'd like to increase the number of employees I can track. Can you please provide more information?";
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" style={{backgroundColor: "#F5F3F0"}}>
      {AddEmployee}

      {/* Filter bar */}
      <div
        className="px-5 py-3 border-b flex flex-wrap items-center gap-3 flex-shrink-0"
        style={{backgroundColor: "#FDFCF9", borderColor: "#E8E4DF"}}
      >
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{color: "#8490AA"}}
          />
          <input
            value={search}
            onChange={onChangeSearch}
            className="w-full pl-9 pr-3 py-2 rounded-xl text-xs border outline-none"
            style={{backgroundColor: "#F7F5F2", borderColor: "#E8E4DF", color: "#282D4D"}}
            placeholder="Search employees…"
          />
        </div>

        {/* Plan usage */}
        <span className="text-xs text-brand-muted">
          Tracking <span className="font-semibold text-brand-navy">{user.employees}</span> /{" "}
          <span className="font-semibold text-brand-navy">{user.maxEmployees}</span> employees
        </span>

        <div className="flex items-center gap-2 ml-auto">
          <Ordering />
          <button
            onClick={limitReached ? onIncreaseEmployees : onNewEmployee}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{backgroundColor: "#3D52AD"}}
          >
            <UserPlus className="w-3.5 h-3.5" />
            {limitReached ? "Increase Limit" : "Add Employee"}
          </button>
        </div>
      </div>

      {/* Card list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        {loading ? (
          Array.from({length: 5}).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl overflow-hidden animate-pulse"
              style={{border: "1px solid #E8E4DF"}}
            >
              <div className="flex h-1.5">
                {Array.from({length: 4}).map((_, j) => (
                  <div key={j} className="flex-1 bg-brand-cream-dark" />
                ))}
              </div>
              <div className="px-5 py-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-cream-dark flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-brand-cream-dark rounded w-1/4" />
                  <div className="h-3 bg-brand-cream-dark rounded w-1/6" />
                </div>
                <div className="flex gap-8 flex-shrink-0">
                  {Array.from({length: 4}).map((_, j) => (
                    <div key={j} className="flex flex-col items-center gap-1">
                      <div className="h-4 w-10 bg-brand-cream-dark rounded" />
                      <div className="h-2.5 w-14 bg-brand-cream-dark rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        ) : (employees ?? []).length === 0 ? (
          <div className="flex items-center justify-center h-48 text-brand-muted text-sm">No employees found</div>
        ) : (
          (employees ?? []).map((employee) => <Employee key={employee.id} employee={employee} />)
        )}
      </div>

      {/* Footer pagination */}
      <div
        className="flex-shrink-0 px-5 py-3 border-t flex items-center gap-4"
        style={{backgroundColor: "white", borderColor: "#E8E4DF"}}
      >
        <span className="text-xs text-brand-muted grow">
          Total Employees Found: <span className="font-semibold text-brand-navy">{loading ? "…" : (total ?? 0)}</span>
        </span>
        <Paginator page={page} setPage={setPage} loading={loading} total={pages} />
        <span className="grow" />
      </div>
    </div>
  );
};

export default EmployeesPage;
